/**
 * Utility function exports
 */

export * from './date'
export * from './csv'
export * from './geolocation'

// Common utility functions

/**
 * Generate a unique ID
 * Uses crypto.randomUUID when available, falls back to timestamp + random
 */
export function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for environments where crypto.randomUUID is not available
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Delay execution for specified milliseconds
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Capitalize first letter of string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Convert an ALL-CAPS / mixed string to Title Case for display.
 * e.g. "42 NORTH DENTAL" → "42 North Dental", "ASPEN" → "Aspen".
 * Words already containing a lowercase letter are left untouched so
 * intentional casing (e.g. "BioHorizons") is preserved.
 */
export function titleCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/\b([a-z])/g, (_m, c: string) => c.toUpperCase())
}

/**
 * Truncate string to specified length
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return num.toLocaleString()
}

/**
 * Clamp number between min and max
 */
export function clamp(num: number, min: number, max: number): number {
  return Math.min(Math.max(num, min), max)
}

/**
 * Get KPI progress color based on percentage
 */
export function getKPIColor(percent: number): 'red' | 'yellow' | 'green' {
  if (percent < 50) return 'red'
  if (percent < 90) return 'yellow'
  return 'green'
}

/**
 * Get KPI status label based on percentage
 */
export function getKPIStatus(percent: number): string {
  if (percent < 50) return 'Behind'
  if (percent < 90) return 'On Track'
  return 'Achieved'
}

/**
 * Sort array by key
 */
export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]

    if (aVal < bVal) return direction === 'asc' ? -1 : 1
    if (aVal > bVal) return direction === 'asc' ? 1 : -1
    return 0
  })
}

/**
 * Group array by key
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const groupKey = String(item[key])
    if (!groups[groupKey]) {
      groups[groupKey] = []
    }
    groups[groupKey].push(item)
    return groups
  }, {} as Record<string, T[]>)
}
